import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { submissionService } from '../services/submissionService';
import { toastService } from '../services/toastService';
import { addSubmission } from '../reducers/submissionsSlice';
import { setFormDirty } from '../reducers/uiSlice';
import { useSync } from './useSync';
import { syncService } from '../services/syncService';
import { db } from '../lib/database';
import { questionResponseService } from '../services/questionResponseService';
import { getCurrentDateForTimezone } from '../utils/dateUtils';
import { backgroundSyncService } from '../services/backgroundSyncService';
import { getFileFields, separateFormAndFileData, getFileFieldTypes, getFileFieldIdMap } from '../utils/formFileUtils';
import { checkRelevance } from '../utils/calculationEngine';
import { fileUploadService } from '../services/fileUploadService';
import { API_ENDPOINTS } from '../config/api';

export const useFormSubmission = (survey, formValues, locationData, countryCode, nameValues = {}, activeRepeats = {}, submissionId = null, entityId = null, entityAction = null, nonEditableFieldNames = new Set()) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOnline } = useSync();
  const [submissionStatus, setSubmissionStatus] = useState('idle');
  const [submittingAction, setSubmittingAction] = useState(null);

  const handleSubmit = (action = 'close', onSuccess = null) => {
    if (!survey || !survey.questions) {
      toastService.error('Form data is not fully loaded.');
      return;
    }

    if (!locationData || !locationData.latitude || !locationData.longitude) {
      toastService.error('Coordinates not fetched. Cannot submit.');
      return;
    }

    if (submissionStatus === 'submitting') return;
    setSubmissionStatus('submitting');
    setSubmittingAction(action);

    const tempSubmissionId = `temp_${Date.now()}`;
    const submissionTime = getCurrentDateForTimezone();
    const fileFields = getFileFields(survey);
    const { formData: cleanFormData, filesData } = separateFormAndFileData(formValues, fileFields);
    const fieldTypes = getFileFieldTypes(survey, fileFields);
    const fieldIdMap = getFileFieldIdMap(survey, activeRepeats);

    const findEndFieldName = questions => {
      const f = questions?.find(q => q.type?.toLowerCase() === 'end');
      return f ? f.id : null;
    };

    const endFieldId = findEndFieldName(survey.questions);
    const finalFormData = { ...cleanFormData };
    if (endFieldId) {
      finalFormData[endFieldId] = submissionTime;
    }

    // Normalize URL protocols
    survey.questions?.forEach(q => {
      if (q.type?.toUpperCase() === 'URL') {
        const val = finalFormData[q.name] || finalFormData[q.id];
        if (val && typeof val === 'string' && val.trim() !== '' && !val.match(/^https?:\/\//i)) {
          const normalized = `https://${val.trim()}`;
          if (finalFormData[q.id]) finalFormData[q.id] = normalized;
          else if (finalFormData[q.name]) finalFormData[q.name] = normalized;
        }
      }
    });

    const isValidValue = value => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    };

    // Helper to check if a field should be excluded from the payload in edit mode
    const isNonEditableField = (fieldName) => {
      if (!submissionId || nonEditableFieldNames.size === 0) return false;
      return nonEditableFieldNames.has(fieldName);
    };

    const extractFormStructure = (sections, pathPrefix = '') => {
      let collectedAnswers = [];
      let collectedRepeats = [];

      if (!sections || !Array.isArray(sections)) return { answers: collectedAnswers, answers_repeats: collectedRepeats };

      sections.forEach(section => {
        if (section.relevance && !checkRelevance(section, nameValues, pathPrefix)) return;

        const isRepeat = !!section.is_repeat;

        if (isRepeat) {
          const key = pathPrefix ? `${pathPrefix}${section.name}` : section.name;
          const repeatCount = parseInt(activeRepeats?.[key]) || 0;
          const sectionItems = [];

          for (let i = 0; i < repeatCount; i++) {
            const itemPath = `${pathPrefix}${section.name}_${i}_`;
            const itemAnswers = (section.fields || [])
              .filter(field => {
                if (!field) return false;
                const type = field.type?.toLowerCase();
                const val = finalFormData[itemPath + field.name];
                if (['note', 'enumerator'].includes(type)) return false;
                // In edit mode, skip non-editable fields
                if (isNonEditableField(itemPath + field.name)) return false;
                if (['file', 'image', 'multi-file', 'audio', 'video'].includes(type)) {
                  return typeof val === 'string';
                }
                if (field.relevance && !checkRelevance(field, nameValues, itemPath)) return false;
                return isValidValue(val);
              })
              .map(field => {
                const k = itemPath + field.name;
                const value = finalFormData[k];
                let finalValue = value;
                if (field.type?.toLowerCase() === 'geopoint' && typeof value === 'object') {
                  finalValue = `${value.latitude} ${value.longitude} ${value.altitude || 0} ${value.accuracy || 0}`;
                }
                const isSignature = field.type?.toLowerCase() === 'signature';
                const isMultiSelect = Array.isArray(value);

                if (isSignature) {
                  return { form_field_id: field.id, value_json: [String(finalValue)] };
                } else if (isMultiSelect) {
                  return { form_field_id: field.id, value_json: finalValue };
                } else {
                  return { form_field_id: field.id, value: String(finalValue) };
                }
              });

            const childData = extractFormStructure(section.sections, itemPath);
            const itemObj = {
              answers: [...itemAnswers, ...childData.answers]
            };

            if (childData.answers_repeats.length > 0) itemObj.answers_repeats = childData.answers_repeats;

            if (itemObj.answers.length > 0 || itemObj.answers_repeats) {
              sectionItems.push(itemObj);
            }
          }

          if (sectionItems.length > 0) {
            collectedRepeats.push({
              form_section_id: section.id,
              items: sectionItems
            });
          }
        } else {
          const sectAnswers = (section.fields || [])
            .filter(field => {
              if (!field) return false;
              const type = field.type?.toLowerCase();
              const fieldName = pathPrefix ? pathPrefix + field.name : field.name;
              const val = finalFormData[fieldName];
              if (['note', 'enumerator'].includes(type)) return false;
              // In edit mode, skip non-editable fields
              if (isNonEditableField(fieldName)) return false;
              if (['file', 'image', 'multi-file', 'audio', 'video'].includes(type)) {
                return typeof val === 'string';
              }
              if (field.relevance && !checkRelevance(field, nameValues, pathPrefix)) return false;
              return isValidValue(val);
            })
            .map(field => {
              const fieldName = pathPrefix ? pathPrefix + field.name : field.name;
              const value = finalFormData[fieldName];
              let finalValue = value;
              if (field.type?.toLowerCase() === 'geopoint' && typeof value === 'object') {
                finalValue = `${value.latitude} ${value.longitude} ${value.altitude || 0} ${value.accuracy || 0}`;
              }
              const isSignature = field.type?.toLowerCase() === 'signature';
              const isMultiSelect = Array.isArray(value);

              if (isSignature) {
                return { form_field_id: field.id, value_json: [String(finalValue)] };
              } else if (isMultiSelect) {
                return { form_field_id: field.id, value_json: finalValue };
              } else {
                return { form_field_id: field.id, value: String(finalValue) };
              }
            });

          collectedAnswers.push(...sectAnswers);

          const childData = extractFormStructure(section.sections, pathPrefix);
          collectedAnswers.push(...childData.answers);
          collectedRepeats.push(...childData.answers_repeats);
        }
      });

      return { answers: collectedAnswers, answers_repeats: collectedRepeats };
    };

    const { answers: rootAnswers, answers_repeats: rootRepeats } = extractFormStructure(survey.sections);

    survey.questions?.forEach(question => {
      const type = question.type?.toLowerCase();
      const val = finalFormData[question.name] || finalFormData[question.id];
      if (['note', 'enumerator'].includes(type)) return;
      // In edit mode, skip non-editable field types from top-level questions too
      if (isNonEditableField(question.name) || isNonEditableField(question.id)) return;
      if (['file', 'image', 'multi-file', 'audio', 'video'].includes(type)) {
        if (typeof val !== 'string') return;
      }
      if (question.relevance && !checkRelevance(question, nameValues)) return;

      let value = finalFormData[question.name] || finalFormData[question.id];
      const isAlreadyAdded = rootAnswers.find(a => a.form_field_id === question.id);

      if (isValidValue(value) && !isAlreadyAdded) {
        const isMultiSelect = Array.isArray(value);
        rootAnswers.push({
          form_field_id: question.id,
          ...(isMultiSelect ? { value_json: value } : { value: String(value) })
        });
      }
    });

    if (rootAnswers.length === 0 && rootRepeats.length === 0 && Object.keys(filesData).length === 0) {
      toastService.error('Cannot submit an empty form.');
      setSubmissionStatus('idle');
      return;
    }

    const submissionPayload = {
      form_id: survey.id,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      answers: rootAnswers,
      answers_repeats: rootRepeats.length > 0 ? rootRepeats : undefined,
      created_at: submissionTime,
      submission_mode: isOnline ? 'online' : 'offline',
      entity_id: entityId,
      // Use the form's configured default action (e.g. 'create', 'update', 'log')
      // This ensures the submission is recorded with the correct action type on the server.
      entity_action: entityAction || survey?.entity_action_default
    };

    const handleOfflineSave = () => {
      const targetSubmissionId = submissionId || tempSubmissionId;
      const isTempSubmission = targetSubmissionId.startsWith('temp_');
      
      const submissionForDb = {
        id: targetSubmissionId,
        form_id: survey.id,
        formName: survey.title,
        ...locationData,
        answers: cleanFormData,
        syncStatus: 'pending',
        created_at: submissionTime
      };

      const dbAction = submissionId ? db.submissions.put(submissionForDb) : db.submissions.add(submissionForDb);

      return dbAction
        .then(() => questionResponseService.storeFormResponses(submissionForDb))
        .then(() => {
          if (submissionId && isTempSubmission) {
            return db.outbox.where({ tempId: targetSubmissionId }).modify({ 
              payload: { ...submissionPayload, tempId: targetSubmissionId } 
            });
          } else {
            return syncService.addToOutbox({
              type: submissionId ? 'submission_update' : 'submission',
              payload: { ...submissionPayload, tempId: targetSubmissionId },
              endpoint: submissionId ? API_ENDPOINTS.submissions.update(submissionId) : API_ENDPOINTS.submissions.submit,
              method: submissionId ? 'PATCH' : 'POST',
              tempId: targetSubmissionId,
              submissionId: submissionId
            });
          }
        })
        .then(() => {
          if (Object.keys(filesData).length > 0) {
            return fileUploadService.storeFilesOffline(targetSubmissionId, filesData, fieldTypes, fieldIdMap).then(() => fileUploadService.addToOutbox(targetSubmissionId, fieldTypes, fieldIdMap));
          }
        })
        .then(() => {
          if (!submissionId) dispatch(addSubmission(submissionForDb));
          backgroundSyncService.registerBackgroundSync();
          return submissionForDb;
        })
        .catch(error => {
          alert(`Offline Save Error: ${error.message}`);
          toastService.error('Failed to save submission locally.');
          throw error;
        });
    };

    const handleSuccess = (newSubmission, offline = false) => {
      dispatch(setFormDirty(false));
      setSubmissionStatus('submitted');
      toastService.success(offline ? 'Changes saved offline!' : submissionId ? 'Submission updated successfully!' : 'Form submitted successfully!');

      if (onSuccess) onSuccess();

      if (action === 'close') {
        navigate('/forms', { replace: true });
      } else {
        setTimeout(() => setSubmissionStatus('idle'), 1500);
      }
    };

    const isTempSubmission = submissionId && submissionId.startsWith('temp_');

    if (isOnline && !isTempSubmission) {
      const submissionAction = submissionId ? submissionService.updateSubmission(submissionId, submissionPayload) : submissionService.submitForm(submissionPayload);

      submissionAction
        .then(response => {
          const responseData = response.data;
          if (responseData && (responseData.status === false || responseData.success === false)) {
            setSubmissionStatus('idle');
            toastService.error(responseData.message || 'Validation failed. Please check your inputs.');
            return;
          }

          const submissionData = responseData.data || responseData;
          const targetId = submissionId || submissionData.id || submissionData.submission_id;

          if (targetId) {
            const submissionToReturn = {
              ...submissionData,
              formName: survey.title,
              answers: cleanFormData,
              syncStatus: 'synced',
              address: locationData.address
            };

            if (Object.keys(filesData).length > 0) {
              return fileUploadService.uploadFiles(targetId, filesData, fieldTypes, fieldIdMap).then(() => {
                handleSuccess(submissionToReturn, false);
              });
            } else {
              handleSuccess(submissionToReturn, false);
            }
          } else {
            throw new Error('Invalid server response');
          }
        })
        .catch(error => {
          const responseData = error.response?.data;
          const apiStatus = error.response?.status;
          const errMsg = responseData?.message || error.message;

          if (error.response && apiStatus >= 400 && apiStatus < 500) {
            setSubmissionStatus('idle');
            toastService.error(errMsg);
            return;
          }

          console.error(`Submission Fallback Triggered: ${errMsg}`, error);

          toastService.warning('Online submission failed. Saving offline.');
          handleOfflineSave()
            .then(offlineSubmission => handleSuccess(offlineSubmission, true))
            .catch(() => setSubmissionStatus('idle'));
        });
    } else {
      handleOfflineSave()
        .then(offlineSubmission => handleSuccess(offlineSubmission, true))
        .catch(() => setSubmissionStatus('idle'));
    }
  };

  return { handleSubmit, submissionStatus, submittingAction };
};
